import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';
import { apiUrl } from '../lib/api';
import { getGuiaById } from '../lib/guiasAgenciasStorage';
import { getExperiencias } from '../lib/experienciasStorage';
import {
  getExcursoesFromStorage as getSharedExcursoesFromStorage,
  saveExcursoesToStorage as saveSharedExcursoesToStorage,
  resolveExcursaoStatus,
  type Excursao as SharedExcursao,
  type MotivoCancelamentoExcursao,
} from '../../../shared/lib/excursaoStorage';
import { addNotification } from '../../../shared/lib/notificationStorage';
import { findClienteByEmail } from '../lib/clientesStorage';
import {
  buildClienteLines,
  buildReservasGrupos,
  buildValorReservasLines,
  computeFinanceiroExcursao,
  formatBRL,
  motivoCancelamentoLabel,
  whatsappHref,
} from '../lib/excursaoDashboardHelpers';

type StatusExcursao = 'em formação' | 'confirmada' | 'cancelada' | 'concluída';

interface ExcursaoApi {
  id: string;
  date: string;
  status: string;
  city: string;
  maxPessoas: number;
  quorumMinimo: number;
  valorPorPessoa: number;
  inscritos: number;
  horarioSaida: string;
  pontoPartida?: string;
  gps?: string;
  guiaId?: string;
  guiaEmail?: string;
  title?: string;
  subtitle?: string;
  experiences?: number[];
  experienceAdminIds?: string[];
  valorGuia?: number;
  clienteIds?: string[];
  criadaPor?: string;
  hasTransport?: boolean;
  arrecadadoPorGrupo?: number;
  reservasGrupos?: SharedExcursao['reservasGrupos'];
  cancelamentoMotivo?: MotivoCancelamentoExcursao;
  cancelamentoMensagem?: string;
  cancelamentoEm?: string;
  comprovanteRessarcimentoPix?: string;
  comprovanteRessarcimentoNomeArquivo?: string;
  createdAt?: string;
}

interface ExcursaoRow {
  id: string;
  nomePasseio: string;
  data: string;
  dataIso: string;
  participantes: string;
  status: StatusExcursao;
  raw: SharedExcursao;
  clientesText: string;
  valorReservasText: string;
  valorPorPessoa: number;
  total: number;
  pagamentoGuia: number;
  lucro: number;
  guiaNome: string;
  embarque: string;
}

function ordenarExcursoes(rows: ExcursaoRow[]): ExcursaoRow[] {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const proximas = rows.filter((r) => {
    if (r.status === 'cancelada') return false;
    const d = new Date(r.dataIso);
    d.setHours(0, 0, 0, 0);
    return d >= hoje && (r.status === 'confirmada' || r.status === 'em formação');
  });
  const concluidas = rows.filter((r) => r.status === 'concluída');
  const canceladas = rows.filter((r) => r.status === 'cancelada');
  const sortByDateAsc = (a: ExcursaoRow, b: ExcursaoRow) =>
    new Date(a.dataIso + 'T12:00').getTime() - new Date(b.dataIso + 'T12:00').getTime();
  const sortByDateDesc = (a: ExcursaoRow, b: ExcursaoRow) =>
    new Date(b.dataIso + 'T12:00').getTime() - new Date(a.dataIso + 'T12:00').getTime();
  proximas.sort(sortByDateAsc);
  concluidas.sort(sortByDateDesc);
  canceladas.sort(sortByDateDesc);
  return [...proximas, ...concluidas, ...canceladas];
}

function getGuiaVinculadoLabel(exc: SharedExcursao): string {
  const guia = exc.guiaId ? getGuiaById(exc.guiaId) : null;
  if (guia?.nome) return guia.nome;
  const guiaEmail = String(exc.guiaEmail ?? '').trim();
  if (guiaEmail) return guiaEmail;
  const guiaId = String(exc.guiaId ?? '').trim();
  if (!guiaId) return 'Indefinido';
  if (guiaId === 'guia_testando') return 'Guia Testando';
  return guiaId;
}

function mergeApiComStorage(api: ExcursaoApi, storage: SharedExcursao | undefined): SharedExcursao {
  if (storage) {
    const merged = {
      ...storage,
      ...api,
      experienceAdminIds: api.experienceAdminIds ?? storage.experienceAdminIds,
      reservasGrupos: api.reservasGrupos ?? storage.reservasGrupos,
    } as SharedExcursao;
    if (storage.status === 'cancelada' || api.status === 'cancelada' || api.status === 'cancelado') {
      merged.status = 'cancelada';
    }
    return { ...merged, status: resolveExcursaoStatus(merged) };
  }
  const base: SharedExcursao = {
    id: api.id,
    date: api.date,
    status: api.status === 'cancelada' || api.status === 'cancelado' ? 'cancelada' : 'em formação',
    city: api.city,
    maxPessoas: api.maxPessoas ?? 12,
    quorumMinimo: api.quorumMinimo ?? 4,
    valorPorPessoa: api.valorPorPessoa ?? 0,
    inscritos: api.inscritos ?? 0,
    horarioSaida: api.horarioSaida || '09:00',
    pontoPartida: api.pontoPartida ?? '',
    gps: api.gps ?? '',
    experiences: api.experiences ?? [],
    experienceAdminIds: api.experienceAdminIds,
    hasTransport: Boolean(api.hasTransport),
    criadaPor: (api.criadaPor as SharedExcursao['criadaPor']) ?? 'admin',
    title: api.title,
    subtitle: api.subtitle,
    valorGuia: api.valorGuia,
    clienteIds: api.clienteIds,
    arrecadadoPorGrupo: api.arrecadadoPorGrupo,
    reservasGrupos: api.reservasGrupos,
    cancelamentoMotivo: api.cancelamentoMotivo,
    cancelamentoMensagem: api.cancelamentoMensagem,
    cancelamentoEm: api.cancelamentoEm,
    comprovanteRessarcimentoPix: api.comprovanteRessarcimentoPix,
    comprovanteRessarcimentoNomeArquivo: api.comprovanteRessarcimentoNomeArquivo,
    guiaId: api.guiaId,
    guiaEmail: api.guiaEmail,
    createdAt: api.createdAt ?? new Date().toISOString(),
  };
  return { ...base, status: resolveExcursaoStatus(base) };
}

export default function ExcursoesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ExcursaoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [buscaGuia, setBuscaGuia] = useState('');
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [pagina, setPagina] = useState(1);
  const [infoTarget, setInfoTarget] = useState<SharedExcursao | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SharedExcursao | null>(null);
  const [deleteMotivo, setDeleteMotivo] = useState<MotivoCancelamentoExcursao | ''>('');
  const [deleteMensagem, setDeleteMensagem] = useState('');

  const syncSharedToApi = async () => {
    try {
      const list = Object.values(getSharedExcursoesFromStorage());
      await fetch(apiUrl('/api/excursoes/sync?source=admin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(list),
      });
    } catch {
      /* best effort */
    }
  };

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      await syncSharedToApi();
      const experiencias = getExperiencias();
      const experienceTitleByNumericId = new Map<number, string>();
      const experienceTitleByAdminId = new Map<string, string>();
      experiencias.forEach((exp, idx) => {
        experienceTitleByNumericId.set(idx + 1, exp.titulo);
        if (exp.id) experienceTitleByAdminId.set(exp.id, exp.titulo);
      });

      const res = await fetch(apiUrl('/api/excursoes'));
      let list: ExcursaoApi[] = res.ok ? await res.json() : [];
      const fromShared = Object.values(getSharedExcursoesFromStorage()) as ExcursaoApi[];
      const byId = new Map<string, ExcursaoApi>();
      list.forEach((x) => {
        if (x?.id) byId.set(x.id, x);
      });
      fromShared.forEach((x) => {
        if (x?.id && !byId.has(x.id)) byId.set(x.id, x);
      });
      list = Array.from(byId.values());

      const storage = getSharedExcursoesFromStorage();

      const mapped: ExcursaoRow[] = list.map((e) => {
        const raw = mergeApiComStorage(e, storage[e.id]);
        const dataStr = raw.date || '';
        const [y, m, d] = dataStr.split('-');
        const dataFormatada = y && m && d ? `${d}/${m}/${y}` : dataStr;
        const status: StatusExcursao = resolveExcursaoStatus(raw);

        const nomesPorAdmin =
          raw.experienceAdminIds?.map((aid) => experienceTitleByAdminId.get(aid)?.trim()).filter(Boolean) ?? [];
        const nomesAtividades =
          nomesPorAdmin.length > 0
            ? (nomesPorAdmin as string[])
            : (raw.experiences || [])
                .map((id) => experienceTitleByNumericId.get(id))
                .filter((v): v is string => Boolean(v && v.trim()));
        const nomePasseioMesclado =
          nomesAtividades.length > 0 ? nomesAtividades.join(' + ') : raw.title || raw.city || 'Passeio';

        const grupos = buildReservasGrupos(raw);
        const fin = computeFinanceiroExcursao(raw);
        const inscritos = Number(raw.inscritos) || 0;
        const vagas = Number(raw.maxPessoas) || 0;

        return {
          id: raw.id,
          nomePasseio: nomePasseioMesclado,
          data: dataFormatada,
          dataIso: raw.date || '',
          participantes: `${inscritos}/${vagas}`,
          status,
          raw,
          clientesText: buildClienteLines(grupos, findClienteByEmail),
          valorReservasText: buildValorReservasLines(grupos),
          valorPorPessoa: fin.valorPorPessoa,
          total: fin.total,
          pagamentoGuia: fin.pagamentoGuia,
          lucro: fin.lucro,
          guiaNome: getGuiaVinculadoLabel(raw),
          embarque: raw.city || '—',
        };
      });

      setRows(ordenarExcursoes(mapped));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useRefetchWhenCatalogChanges(() => {
    void loadRows();
  });

  const statusConfig: Record<StatusExcursao, { bg: string; text: string; label: string }> = {
    'em formação': { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Em formação' },
    confirmada: { bg: 'bg-green-100', text: 'text-green-800', label: 'Confirmado' },
    cancelada: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelado' },
    concluída: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Concluída' },
  };

  const filtradas = rows.filter((r) => {
    if (filtroStatus && r.status !== filtroStatus) return false;
    if (buscaGuia.trim()) {
      const q = buscaGuia.trim().toLowerCase();
      const hitGuia = r.guiaNome.toLowerCase().includes(q);
      const hitCliente = r.clientesText.toLowerCase().includes(q);
      if (!hitGuia && !hitCliente) return false;
    }
    return true;
  });

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / itensPorPagina));

  const handleSaveComprovante = (excId: string, dataUrl: string, nome: string) => {
    const map = getSharedExcursoesFromStorage();
    const cur = map[excId];
    if (!cur) return;
    map[excId] = {
      ...cur,
      comprovanteRessarcimentoPix: dataUrl,
      comprovanteRessarcimentoNomeArquivo: nome,
    };
    saveSharedExcursoesToStorage(map);
    void syncSharedToApi();
    setInfoTarget(map[excId]);
    setRows((prev) =>
      ordenarExcursoes(
        prev.map((r) =>
          r.id === excId ? { ...r, raw: map[excId] } : r
        )
      )
    );
  };

  const paginatedFixed = filtradas.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);

  const handleOpenCancel = (raw: SharedExcursao) => {
    setDeleteMotivo('');
    setDeleteMensagem('');
    setDeleteTarget(raw);
  };

  const notifyCancelamento = (raw: SharedExcursao, motivo: MotivoCancelamentoExcursao, mensagem: string) => {
    const tituloExc = raw.title || raw.city || raw.id;
    const textoMotivo = motivoCancelamentoLabel(motivo);
    const body = `${tituloExc} foi cancelada.\nMotivo: ${textoMotivo}\n\n${mensagem.trim()}`;
    const emails = new Set<string>();
    raw.clienteIds?.forEach((id) => emails.add(id.trim().toLowerCase()));
    buildReservasGrupos(raw).forEach((g) => {
      if (g.responsavelEmail) emails.add(g.responsavelEmail.trim().toLowerCase());
    });
    emails.forEach((em) => {
      if (em)
        addNotification({
          toEmail: em,
          type: 'warning',
          title: 'Excursão cancelada',
          description: body,
          metadata: { excursaoId: raw.id, motivo: textoMotivo },
        });
    });
    const guiaMail = String(raw.guiaEmail ?? '').trim();
    if (guiaMail) {
      addNotification({
        toEmail: guiaMail,
        type: 'warning',
        title: 'Excursão cancelada (guia)',
        description: body,
        metadata: { excursaoId: raw.id },
      });
    }
  };

  const handleConfirmCancel = async () => {
    if (!deleteTarget) return;
    if (!deleteMotivo) {
      window.alert('Selecione o motivo do cancelamento.');
      return;
    }
    if (!deleteMensagem.trim()) {
      window.alert('Informe a mensagem que será enviada aos clientes.');
      return;
    }
    const map = getSharedExcursoesFromStorage();
    const cur = map[deleteTarget.id] ?? deleteTarget;
    notifyCancelamento(cur, deleteMotivo, deleteMensagem);
    map[cur.id] = {
      ...cur,
      status: 'cancelada',
      cancelamentoMotivo: deleteMotivo,
      cancelamentoMensagem: deleteMensagem.trim(),
      cancelamentoEm: new Date().toISOString(),
    };
    saveSharedExcursoesToStorage(map);
    await syncSharedToApi();
    setDeleteTarget(null);
    setDeleteMotivo('');
    setDeleteMensagem('');
    void loadRows();
  };

  return (
    <AdminLayout>
      <div className="w-full">
        <div className="sticky top-0 z-20 px-4 sm:px-6 py-6 border-b border-gray-200 bg-white shadow-sm">
          <nav className="text-sm text-gray-500 mb-1">
            <Link to="/dashboard" className="hover:text-gray-700">
              Home
            </Link>
            {' > '}
            <span className="text-gray-800">Excursão</span>
          </nav>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Excursão</h1>
            <Link
              to="/excursoes/nova"
              className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600"
            >
              Nova Excursão
            </Link>
          </div>
        </div>

        <div className="px-3 sm:px-6 py-6">
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Nome do guia ou cliente"
              value={buscaGuia}
              onChange={(e) => {
                setBuscaGuia(e.target.value);
                setPagina(1);
              }}
              className="py-2 px-3 rounded-lg border border-gray-300 min-w-[200px] w-72 sm:w-80"
            />
            <select
              value={filtroStatus}
              onChange={(e) => {
                setFiltroStatus(e.target.value);
                setPagina(1);
              }}
              className="py-2 px-3 rounded-lg border border-gray-300"
            >
              <option value="">Status</option>
              {(Object.keys(statusConfig) as StatusExcursao[]).map((s) => (
                <option key={s} value={s}>
                  {statusConfig[s].label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="py-8 text-center text-gray-500">Carregando...</div>
          ) : filtradas.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              Nenhuma excursão encontrada.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-[1400px] w-full text-sm">
                  <thead>
                    <tr className="bg-orange-500 text-white">
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">Data</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase min-w-[140px]">Nome do passeio</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase min-w-[200px]">Clientes</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase min-w-[100px]">Valor reservas</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">R$/pessoa</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">Total</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">Pag. guia</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase whitespace-nowrap">Lucro</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase min-w-[100px]">Guia</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase min-w-[120px]">Embarque</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase">Particip.</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase">Status</th>
                      <th className="px-2 py-3 text-left text-xs font-semibold uppercase w-[140px]">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedFixed.map((r, idx) => {
                      const sc = statusConfig[r.status];
                      const grupos = buildReservasGrupos(r.raw);
                      return (
                        <tr
                          key={r.id}
                          className={`${
                            ['bg-white', 'bg-gray-50', 'bg-orange-50/40'][idx % 3]
                          } border-b border-gray-100`}
                        >
                          <td className="px-2 py-3 text-gray-800 whitespace-nowrap align-top">{r.data}</td>
                          <td className="px-2 py-3 font-medium text-gray-900 align-top">{r.nomePasseio}</td>
                          <td className="px-2 py-3 text-gray-700 align-top">
                            <div className="whitespace-pre-line space-y-1">
                              {grupos.map((g, gi) => {
                                const cad = g.responsavelEmail ? findClienteByEmail(g.responsavelEmail) : undefined;
                                const nome =
                                  g.responsavelNome?.trim() ||
                                  [cad?.nome, cad?.sobrenome].filter(Boolean).join(' ').trim() ||
                                  g.responsavelEmail ||
                                  'Cliente';
                                const tel = (g.telefone?.trim() || cad?.telefone?.trim() || '').trim();
                                const wa = whatsappHref(tel || undefined);
                                return (
                                  <div key={gi}>
                                    {nome} ({g.quantidadePessoas})
                                    {tel ? (
                                      <>
                                        {' '}
                                        —{' '}
                                        <a
                                          href={wa}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-green-700 underline"
                                        >
                                          {tel}
                                        </a>
                                      </>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td className="px-2 py-3 text-gray-800 align-top whitespace-pre-line">{r.valorReservasText}</td>
                          <td className="px-2 py-3 align-top">{formatBRL(r.valorPorPessoa)}</td>
                          <td className="px-2 py-3 font-semibold text-gray-900 align-top">{formatBRL(r.total)}</td>
                          <td className="px-2 py-3 text-red-600 font-medium align-top">{formatBRL(r.pagamentoGuia)}</td>
                          <td className="px-2 py-3 text-blue-600 font-semibold align-top">{formatBRL(r.lucro)}</td>
                          <td className="px-2 py-3 text-gray-700 align-top">{r.guiaNome}</td>
                          <td className="px-2 py-3 text-gray-700 align-top">{r.embarque}</td>
                          <td className="px-2 py-3 text-gray-700 align-top">{r.participantes}</td>
                          <td className="px-2 py-3 align-top">
                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${sc.bg} ${sc.text}`}>
                              {sc.label}
                            </span>
                          </td>
                          <td className="px-2 py-3 align-top">
                            <div className="flex items-center gap-1 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setInfoTarget(r.raw)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="Informações"
                                aria-label="Informações"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => navigate(`/excursoes/${r.id}/editar`)}
                                disabled={r.status === 'cancelada' || r.status === 'concluída'}
                                className="p-1.5 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg disabled:opacity-40"
                                title="Editar"
                                aria-label="Editar"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenCancel(r.raw)}
                                disabled={r.status === 'cancelada' || r.status === 'concluída'}
                                className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-40"
                                title="Cancelar excursão"
                                aria-label="Cancelar excursão"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Itens por página</span>
                  <select
                    value={itensPorPagina}
                    onChange={(e) => {
                      setItensPorPagina(Number(e.target.value));
                      setPagina(1);
                    }}
                    className="py-1.5 px-2 rounded border border-gray-300 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setPagina((p) => Math.max(1, p - 1))}
                    disabled={pagina <= 1}
                    className="px-2 py-1 rounded border border-gray-300 text-sm disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-600 px-2">
                    Página {pagina} / {totalPaginas}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                    disabled={pagina >= totalPaginas}
                    className="px-2 py-1 rounded border border-gray-300 text-sm disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {infoTarget && (
        <div
          className="fixed inset-0 z-[300] bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setInfoTarget(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const fin = computeFinanceiroExcursao(infoTarget);
              const grupos = buildReservasGrupos(infoTarget);
              return (
                <>
                  <h2 className="text-lg font-bold text-gray-900 mb-2">Detalhes da excursão</h2>
                  <p className="text-sm text-gray-600 mb-4">{infoTarget.title || infoTarget.city}</p>
                  <div className="space-y-3 text-sm border border-gray-100 rounded-xl p-4 bg-gray-50">
                    <p>
                      <span className="font-medium">Data:</span> {infoTarget.date} · <span className="font-medium">Saída:</span>{' '}
                      {infoTarget.horarioSaida}
                    </p>
                    <p>
                      <span className="font-medium">Embarque:</span> {infoTarget.city}
                    </p>
                    <p>
                      <span className="font-medium">Participantes:</span> {infoTarget.inscritos}/{infoTarget.maxPessoas}
                    </p>
                    <p>
                      <span className="font-medium">Valor por pessoa:</span> {formatBRL(fin.valorPorPessoa)}
                    </p>
                    <p>
                      <span className="font-medium">Total arrecadado:</span> {formatBRL(fin.total)}
                    </p>
                    <p className="text-red-600">
                      <span className="font-medium">Pagamento guia (estimado):</span> {formatBRL(fin.pagamentoGuia)}
                    </p>
                    <p className="text-blue-600">
                      <span className="font-medium">Lucro estimado:</span> {formatBRL(fin.lucro)}
                    </p>
                  </div>
                  <h3 className="font-semibold text-gray-900 mt-5 mb-2">Reservas / grupos</h3>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                    {grupos.map((g, i) => (
                      <li key={i}>
                        {g.responsavelNome || g.responsavelEmail || 'Grupo'} — {g.quantidadePessoas} pessoa(s) —{' '}
                        {formatBRL(g.valorTotalVenda ?? 0)}
                      </li>
                    ))}
                  </ul>
                  {infoTarget.status === 'cancelada' && (
                    <div className="mt-6 p-4 border border-amber-200 rounded-xl bg-amber-50">
                      <h3 className="font-semibold text-amber-900">Ressarcimento (manual)</h3>
                      <p className="text-xs text-amber-800 mt-1">
                        Anexe o comprovante PIX após realizar o ressarcimento ao cliente.
                      </p>
                      {infoTarget.comprovanteRessarcimentoPix ? (
                        <div className="mt-3">
                          <p className="text-xs text-gray-600 mb-1">
                            {infoTarget.comprovanteRessarcimentoNomeArquivo || 'Comprovante anexado'}
                          </p>
                          <img
                            src={infoTarget.comprovanteRessarcimentoPix}
                            alt="Comprovante"
                            className="max-h-48 rounded border border-gray-200"
                          />
                        </div>
                      ) : null}
                      <label className="block mt-3 text-xs font-medium text-gray-700">
                        Anexar / substituir comprovante
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          className="mt-1 block w-full text-sm"
                          onChange={(ev) => {
                            const f = ev.target.files?.[0];
                            if (!f || !infoTarget) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              const r = String(reader.result || '');
                              if (r.startsWith('data:')) handleSaveComprovante(infoTarget.id, r, f.name);
                            };
                            reader.readAsDataURL(f);
                          }}
                        />
                      </label>
                    </div>
                  )}
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setInfoTarget(null)}
                      className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800"
                    >
                      Fechar
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-[300] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-red-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 bg-red-50 border-b border-red-200 flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-red-100 text-red-700">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
              </span>
              <h3 className="text-lg font-semibold text-red-900">Cancelar excursão</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-700">
                A excursão será marcada como <strong>cancelada</strong>. Os clientes e o guia receberão a notificação com a
                mensagem abaixo.
              </p>
              <label className="block text-sm text-gray-700">
                Motivo
                <select
                  value={deleteMotivo}
                  onChange={(e) => setDeleteMotivo(e.target.value as MotivoCancelamentoExcursao | '')}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                >
                  <option value="">Selecione…</option>
                  <option value="meteorologia">Condições meteorológicas</option>
                  <option value="quorum_insuficiente">Quórum insuficiente</option>
                  <option value="outro">Outro</option>
                </select>
              </label>
              <label className="block text-sm text-gray-700">
                Mensagem para os clientes
                <textarea
                  value={deleteMensagem}
                  onChange={(e) => setDeleteMensagem(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 min-h-[100px]"
                  placeholder="Texto exibido na notificação…"
                />
              </label>
            </div>
            <div className="px-5 pb-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmCancel()}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
              >
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
